/**
 * Consolidated MainDetails Form Permissions
 * 
 * This file contains all permission configurations for the MainDetails form components:
 * - ProductSpecificationInformation
 * - ProductRelatedInformation  
 * - VendorFacilityInformation
 * - VendorRelatedInformation
 * 
 * Each permission object contains field-level access control with:
 * - isEditable: boolean - Controls if field is editable by user
 * - isMandatory: boolean - Controls if field is required for form submission
 */

import AuthService from "@/services/authService";
import { getUserRole, getFieldPermissionByRole } from "@/utils/roleBasedFieldPermissions";
import { UserDetails } from "@/context/data.types";

export interface FieldPermission {
  isEditable: boolean;
  isMandatory: boolean;
}

export interface PermissionObject {
  [fieldName: string]: FieldPermission;
}


// ============================================================================
// PRODUCT SPECIFICATION INFORMATION PERMISSIONS
// ============================================================================

/**
 * Get Product Specification Information permissions based on user role and specification type
 * @param userDetails - User details object
 * @param specificationType - Product specification type (e.g., "Food & Beverages", "HABA", etc.)
 * @returns Permission object for Product Specification Information fields
 */
export const getProductSpecificationInformationPermissions = (userDetails: UserDetails | null, specificationType?: string): PermissionObject => {
  const userRole = getUserRole(userDetails);
  const permissions = AuthService.fetchPermissions("MainDetails");
  
  if (!permissions) {
    // Fallback to default permissions
    return {
      productSpecificationType: { isEditable: false, isMandatory: true },
      artworkDeadline: { isEditable: true, isMandatory: true },
    };
  }

  return {
    productSpecificationType: getFieldPermissionByRole(permissions.productSpecificationType, userRole, specificationType),
    artworkDeadline: getFieldPermissionByRole(permissions.artworkDeadline, userRole, specificationType),
  };
};


// ============================================================================
// PRODUCT RELATED INFORMATION PERMISSIONS
// ============================================================================

/**
 * Get Product Related Information permissions based on user role and specification type
 * @param userDetails - User details object
 * @param specificationType - Product specification type (e.g., "Food & Beverages", "HABA", etc.)
 * @returns Permission object for Product Related Information fields
 */
export const getProductRelatedInformationPermissions = (userDetails: UserDetails | null, specificationType?: string): PermissionObject => {
  const userRole = getUserRole(userDetails);
  const permissions = AuthService.fetchPermissions("MainDetails");
  
  if (!permissions) {
    // Fallback to default permissions with role-based mandatory fields
    const isTJUser = userRole === "TJ";
    return {
      sku: { isEditable: false, isMandatory: true },
      productName: { isEditable: true, isMandatory: true },
      legacyIncomplete: { isEditable: true, isMandatory: false },
      isMultiPackProduct: { isEditable: true, isMandatory: true },
      numberOfFlavorsVarieties: { isEditable: true, isMandatory: false },
      weightVolumePackQty: { isEditable: true, isMandatory: true },
      weightUOM: { isEditable: true, isMandatory: true },
      category: { isEditable: true, isMandatory: true },
      categoryManager: { isEditable: true, isMandatory: true },
      buyer: { isEditable: false, isMandatory: true },
      qaComplianceManager: { isEditable: false, isMandatory: true },
      productCreator: { isEditable: true, isMandatory: true },
      trafficManager: { isEditable: isTJUser, isMandatory: isTJUser },
      nutritionist: { isEditable: false, isMandatory: true },
      regulatoryAgenciesOversight: { isEditable: true, isMandatory: true },
      foodSaftyHaccpPlan: { isEditable: true, isMandatory: true },
      ftlList: { isEditable: true, isMandatory: true },
      warehousesWhereSupplied: { isEditable: true, isMandatory: true },
      projectType: { isEditable: isTJUser, isMandatory: isTJUser },
      launchDate: { isEditable: isTJUser, isMandatory: isTJUser },
      regulatoryGovernance: { isEditable: isTJUser, isMandatory: isTJUser },
      nationalDrugCodeNumber: { isEditable: true, isMandatory: false },
      certifiedOrganic: { isEditable: false, isMandatory: false },
      retailPrice: { isEditable: false, isMandatory: false },
      productSize: { isEditable: false, isMandatory: false },
      casePack: { isEditable: false, isMandatory: false },
    };
  }

  return {
    sku: getFieldPermissionByRole(permissions.sku, userRole, specificationType),
    productName: getFieldPermissionByRole(permissions.productName, userRole, specificationType),
    legacyIncomplete: getFieldPermissionByRole(permissions.legacyIncomplete, userRole, specificationType),
    isMultiPackProduct: getFieldPermissionByRole(permissions.isMultiPackProduct, userRole, specificationType),
    numberOfFlavorsVarieties: getFieldPermissionByRole(permissions.numberOfFlavorsVarieties, userRole, specificationType),
    weightVolumePackQty: getFieldPermissionByRole(permissions.weightVolumePackQty, userRole, specificationType),
    weightUOM: getFieldPermissionByRole(permissions.weightUOM, userRole, specificationType),
    category: getFieldPermissionByRole(permissions.category, userRole, specificationType),
    categoryManager: getFieldPermissionByRole(permissions.categoryManager, userRole, specificationType),
    buyer: (() => {
      const base = getFieldPermissionByRole(permissions.buyer, userRole, specificationType);
      return { ...base, isEditable: false };
    })(),
    qaComplianceManager: (() => {
      const base = getFieldPermissionByRole(permissions.qaComplianceManager, userRole, specificationType);
      return { ...base, isEditable: false };
    })(),
    productCreator: getFieldPermissionByRole(permissions.productCreator, userRole, specificationType),
    trafficManager: getFieldPermissionByRole(permissions.trafficManager, userRole, specificationType),
    nutritionist: (() => {
      const base = getFieldPermissionByRole(permissions.nutritionist, userRole, specificationType);
      return { ...base, isEditable: false };
    })(),
    regulatoryAgenciesOversight: getFieldPermissionByRole(permissions.regulatoryAgenciesOversight, userRole, specificationType),
    foodSaftyHaccpPlan: getFieldPermissionByRole(permissions.foodSaftyHaccpPlan, userRole, specificationType),
    ftlList: getFieldPermissionByRole(permissions.ftlList, userRole, specificationType),
    warehousesWhereSupplied: getFieldPermissionByRole(permissions.warehousesWhereSupplied, userRole, specificationType),
    projectType: (() => {
    const base = getFieldPermissionByRole(permissions.projectType, userRole, specificationType);
    if (userRole === 'VENDOR') return { ...base, isMandatory: false };
    return base;
  })(),
    launchDate: (() => {
    const base = getFieldPermissionByRole(permissions.launchDate, userRole, specificationType);
    if (userRole === 'VENDOR') return { ...base, isMandatory: false };
    return base;
  })(),
    regulatoryGovernance: (() => {
    const base = getFieldPermissionByRole(permissions.regulatoryGovernance, userRole, specificationType);
    if (userRole === 'VENDOR') return { ...base, isMandatory: false };
    return base;
  })(),
    nationalDrugCodeNumber: getFieldPermissionByRole(permissions.nationalDrugCodeNumber, userRole, specificationType),
    certifiedOrganic: getFieldPermissionByRole(permissions.certifiedOrganic, userRole, specificationType),
    retailPrice: (() => {
      const base = getFieldPermissionByRole(permissions.retailPrice, userRole, specificationType);
      if (userRole === 'VENDOR') return { ...base, isMandatory: false };
      return base;
    })(),
    productSize: getFieldPermissionByRole(permissions.productSize, userRole, specificationType),
    casePack: getFieldPermissionByRole(permissions.casePack, userRole, specificationType),
  };
};


// ============================================================================
// PHOTOS OF ITEM IN RETAIL PACKAGING PERMISSIONS
// ============================================================================

/**
 * Get Photos of Item in Retail Packaging permissions based on user role and specification type
 * @param userDetails - User details object
 * @param specificationType - Product specification type (e.g., "Food & Beverages", "HABA", etc.)
 * @returns Permission object for Photos of Item in Retail Packaging fields
 */
export const getPhotosofItemInRetailPackagingPermissions = (userDetails: UserDetails | null, specificationType?: string): PermissionObject => {
  const userRole = getUserRole(userDetails);
  const permissions = AuthService.fetchPermissions("MainDetails");
  
  if (!permissions) {
    // Fallback to default permissions
    return {
      retailPackaging: { isEditable: true, isMandatory: true },
      outOfPackaging: { isEditable: true, isMandatory: true },
      prepared: { isEditable: true, isMandatory: true },
      shelfLifeStudy: { isEditable: true, isMandatory: true },
    };
  }

  return {
    retailPackaging: getFieldPermissionByRole(permissions?.retailPackaging, userRole, specificationType),
    outOfPackaging: getFieldPermissionByRole(permissions?.outOfPackaging, userRole, specificationType),
    prepared: getFieldPermissionByRole(permissions?.prepared, userRole, specificationType),
    shelfLifeStudy: getFieldPermissionByRole(permissions?.shelfLifeStudy, userRole, specificationType),
  };
};

// ============================================================================
// VENDOR FACILITY INFORMATION PERMISSIONS
// ============================================================================

/**
 * Get Vendor Facility Information permissions based on user role and specification type
 * @param userDetails - User details object
 * @param specificationType - Product specification type (e.g., "Food & Beverages", "HABA", etc.)
 * @returns Permission object for Vendor Facility Information fields
 */
export const getVendorFacilityInformationPermissions = (userDetails: UserDetails | null, specificationType?: string): PermissionObject => {
  const userRole = getUserRole(userDetails);
  const permissions = AuthService.fetchPermissions("MainDetails");
  
  if (!permissions) {
    // Fallback to default permissions
    return {
      productPortalId: { isEditable: false, isMandatory: true  },
      orbcSiteCode: {isEditable: false, isMandatory: true},
      siteCode: { isEditable: false, isMandatory: false },
      siteName: { isEditable: false, isMandatory: false },
      address1: { isEditable: false, isMandatory: false },
      address2: { isEditable: false, isMandatory: false },
      city: { isEditable: false, isMandatory: false },
      state: { isEditable: false, isMandatory: false },
      country: { isEditable: false, isMandatory: false },
      zipCodePostalCode: { isEditable: false, isMandatory: false },
    };
  }

  return {
    productPortalId: { isEditable: false, isMandatory:  true },
    orbcSiteCode: {isEditable: false, isMandatory: true},
    siteCode: getFieldPermissionByRole(permissions.siteCode, userRole, specificationType),
    siteName: getFieldPermissionByRole(permissions.siteName, userRole, specificationType),
    address1: getFieldPermissionByRole(permissions.address1, userRole, specificationType),
    address2: getFieldPermissionByRole(permissions.address2, userRole, specificationType),
    city: getFieldPermissionByRole(permissions.city, userRole, specificationType),
    state: getFieldPermissionByRole(permissions.state, userRole, specificationType),
    country: getFieldPermissionByRole(permissions.country, userRole, specificationType),
    zipCodePostalCode: getFieldPermissionByRole(permissions.zipCodePostalCode, userRole, specificationType),
  };
};


// ============================================================================
// VENDOR RELATED INFORMATION PERMISSIONS
// ============================================================================

/**
 * Get Vendor Related Information permissions based on user role and specification type
 * @param userDetails - User details object
 * @param specificationType - Product specification type (e.g., "Food & Beverages", "HABA", etc.)
 * @returns Permission object for Vendor Related Information fields
 */
export const getVendorRelatedInformationPermissions = (userDetails: UserDetails | null, specificationType?: string): PermissionObject => {
  const userRole = getUserRole(userDetails);
  const permissions = AuthService.fetchPermissions("MainDetails");
  
  if (!permissions) {
    // Fallback to default permissions - vendor fields need to be editable for API data to populate them
    return {
      vendorNumber: { isEditable: true, isMandatory: false },
      vendorName: { isEditable: true, isMandatory: false },
      sites: { isEditable: true, isMandatory: false },
    };
  }

  return {
    vendorNumber: getFieldPermissionByRole(permissions.vendorNumber, userRole, specificationType),
    vendorName: getFieldPermissionByRole(permissions.vendorName, userRole, specificationType),
    sites: getFieldPermissionByRole(permissions.sites, userRole, specificationType),
  };
};


// ============================================================================
// CONSOLIDATED MAIN DETAILS PERMISSIONS
// ============================================================================

/**
 * Get consolidated MainDetails permissions based on user role and specification type
 * @param userDetails - User details object
 * @param specificationType - Product specification type (e.g., "Food & Beverages", "HABA", etc.)
 * @returns Consolidated permission object for all MainDetails sections
 */
export const getMainDetailsPermissions = (userDetails: UserDetails | null, specificationType?: string) => {
  return {
    productSpecificationInformation: getProductSpecificationInformationPermissions(userDetails, specificationType),
    productRelatedInformation: getProductRelatedInformationPermissions(userDetails, specificationType),
    vendorFacilityInformation: getVendorFacilityInformationPermissions(userDetails, specificationType),
    vendorRelatedInformation: getVendorRelatedInformationPermissions(userDetails, specificationType),
    photosofItemInRetailPackaging: getPhotosofItemInRetailPackagingPermissions(userDetails, specificationType),
  };
};


// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get permission for a specific field in a specific section
 * @param section - The form section (e.g., 'productSpecificationInformation')
 * @param fieldName - The field name
 * @param userDetails - User details object
 * @param specificationType - Product specification type (e.g., "Food & Beverages", "HABA", etc.)
 * @returns FieldPermission object or default permissions
 */
export const getFieldPermission = (
  section: string,
  fieldName: string,
  userDetails: UserDetails | null = null,
  specificationType?: string
): FieldPermission => {
  // Get the appropriate section permissions based on user role
  let sectionPermissions: PermissionObject;
  
  switch (section) {
    case 'productSpecificationInformation':
      sectionPermissions = getProductSpecificationInformationPermissions(userDetails, specificationType);
      break;
    case 'productRelatedInformation':
      sectionPermissions = getProductRelatedInformationPermissions(userDetails, specificationType);
      break;
    case 'vendorFacilityInformation':
      sectionPermissions = getVendorFacilityInformationPermissions(userDetails, specificationType);
      break;
    case 'vendorRelatedInformation':
      sectionPermissions = getVendorRelatedInformationPermissions(userDetails, specificationType);
      break;
    default:
      return {
        isEditable: true,
        isMandatory: false,
      };
  }
  
  return sectionPermissions[fieldName] || {
    isEditable: true,
    isMandatory: false,
  };
};

/**
 * Check if a field is editable
 * @param section - The form section
 * @param fieldName - The field name
 * @param userDetails - User details object
 * @param specificationType - Product specification type (e.g., "Food & Beverages", "HABA", etc.)
 * @returns boolean
 */
export const isFieldEditable = (
  section: string,
  fieldName: string,
  userDetails: UserDetails | null = null,
  specificationType?: string
): boolean => {
  return getFieldPermission(section, fieldName, userDetails, specificationType).isEditable;
};

/**
 * Check if a field is mandatory
 * @param section - The form section
 * @param fieldName - The field name
 * @param userDetails - User details object
 * @param specificationType - Product specification type (e.g., "Food & Beverages", "HABA", etc.)
 * @returns boolean
 */
export const isFieldMandatory = (
  section: string,
  fieldName: string,
  userDetails: UserDetails | null = null,
  specificationType?: string
): boolean => {
  return getFieldPermission(section, fieldName, userDetails, specificationType).isMandatory;
};

export default getMainDetailsPermissions;

